import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../firebase/firebaseConfig';
import { firebaseCache } from '../../utils/firebaseCache';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';

const serializePolicy = (policy) => {
  if (!policy) return null;
  const serialized = { ...policy };
  const timestampFields = [
    'createdAt', 'updatedAt', 'commissionUpdatedAt', 'renewalDate',
    'policyStartDate', 'policyEndDate', 'lastReminderDate', 'lastSharedDate', 'expiryDate'
  ];
  timestampFields.forEach(field => {
    if (serialized[field]) {
      if (typeof serialized[field].toDate === 'function') {
        serialized[field] = serialized[field].toDate().toISOString();
      } else if (serialized[field].seconds !== undefined) {
        serialized[field] = new Date(serialized[field].seconds * 1000).toISOString();
      }
    }
  });
  return serialized;
};

export const fetchPolicies = createAsyncThunk('policies/fetchPolicies', async (params) => {
  const agentId = typeof params === 'string' ? params : params?.agentId;

  if (!agentId) {
    throw new Error("fetchPolicies called without agentId");
  }

  const forceRefresh = params?.forceRefresh === true;

  if (forceRefresh) {
    console.log(`🔃 Force refresh requested - bypassing cache and fetching from server for ${agentId}`);
  }

  // Check if this is a paginated call
  const isPaginated = params?.pageSize || params?.lastDoc;

  if (isPaginated) {
    // Use the paginated version
    const cacheKey = firebaseCache.getPolicyCacheKey(
      agentId,
      params.pageSize || 20,
      params.lastDoc ? 'paged' : 'first'
    );

    // Try cache first
    const cachedData = firebaseCache.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const policiesRef = collection(db, `agents/${agentId}/policies`);
    let q = query(
      policiesRef,
      orderBy('createdAt', 'desc'),
      limit(params.pageSize || 20)
    );

    // Add filters
    if (params.filters?.status) {
      q = query(q, where('status', '==', params.filters.status));
    }

    if (params.lastDoc) {
      q = query(q, startAfter(params.lastDoc));
    }

    const snapshot = await getDocs(q);
    const policies = snapshot.docs.map(doc => serializePolicy({ id: doc.id, ...doc.data() }));

    const result = {
      policies,
      lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
      hasMore: snapshot.docs.length === (params.pageSize || 20)
    };

    // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, result, 24 * 60 * 60 * 1000);

    return result;
  }

  // This is a call for all policies (like from Renewals component)
  const cacheKey = `policies_all_${agentId}`;

  // Try cache first
  const cachedData = forceRefresh ? null : firebaseCache.get(cacheKey);
  if (cachedData) {
    console.log(`✅ Returning ${cachedData.length} policies from cache (key: ${cacheKey})`);
    return cachedData;
  }

  console.log(`📥 Cache miss - fetching policies from Firestore (key: ${cacheKey})`);

  const q = query(
    collection(db, `agents/${agentId}/policies`),
    orderBy('createdAt', 'desc')
  );
  // Use default source (cache first, then server) - Firestore persistence handles this
  const snapshot = await getDocs(q);
  const allPolicies = snapshot.docs.map(doc => serializePolicy({ id: doc.id, ...doc.data() }));

  // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
  firebaseCache.set(cacheKey, allPolicies, 24 * 60 * 60 * 1000);

  return allPolicies;
});

/**
 * fetchPoliciesForMultipleUsers - Fetch policies from multiple staff members
 * Used by admins to view aggregated policies from all linked staff
 */
export const fetchPoliciesForMultipleUsers = createAsyncThunk(
  'policies/fetchPoliciesForMultipleUsers',
  async (userIds) => {
    if (!userIds) {
      console.log('No user IDs provided, returning empty array');
      return [];
    }

    const forceRefresh = !Array.isArray(userIds) ? userIds?.forceRefresh === true : false;
    const actualUserIds = !Array.isArray(userIds) ? userIds?.userIds : userIds;

    if (!actualUserIds || actualUserIds.length === 0) {
      return [];
    }

    console.log(`Fetching policies for ${actualUserIds.length} user(s):`, actualUserIds);

    // Create aggregated cache key
    const cacheKey = `policies_aggregated_${actualUserIds.slice().sort().join('_')}`;

    // Try cache first
    const cachedData = forceRefresh ? null : firebaseCache.get(cacheKey);
    if (cachedData) {
      console.log(`✅ Returning ${cachedData.length} policies from cache (key: ${cacheKey})`);
      return cachedData;
    }

    console.log(`Fetching policies for ${actualUserIds.length} user(s):`, actualUserIds);

    // Fetch policies for each user in parallel
    const policiesPromises = actualUserIds.map(async (userId) => {
      try {
        const q = query(
          collection(db, `agents/${userId}/policies`),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const policies = snapshot.docs.map(doc => serializePolicy({
          id: doc.id,
          ...doc.data(),
          agentId: userId, // Add agentId to track which staff member owns this policy
        }));
        console.log(`Fetched ${policies.length} policies for user ${userId}`);
        return policies;
      } catch (error) {
        console.error(`Error fetching policies for user ${userId}:`, error);
        return [];
      }
    });

    // Wait for all promises to resolve
    const policiesArrays = await Promise.all(policiesPromises);

    // Flatten the array of arrays into a single array
    const allPolicies = policiesArrays.flat();

    console.log(`Total policies fetched: ${allPolicies.length}`);

    // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, allPolicies, 24 * 60 * 60 * 1000);

    return allPolicies;
  }
);

export const fetchPoliciesPaginated = createAsyncThunk(
  'policies/fetchPoliciesPaginated',
  async ({ agentId, pageSize = 20, lastDoc = null, searchTerm = "", filters = {} }) => {
    const cacheKey = firebaseCache.getPolicyCacheKey(
      agentId,
      pageSize,
      lastDoc ? 'paged' : 'first'
    );

    // Try cache first
    const cachedData = firebaseCache.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const policiesRef = collection(db, `agents/${agentId}/policies`);
    let q = query(
      policiesRef,
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    // Add filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const policies = snapshot.docs.map(doc => serializePolicy({ id: doc.id, ...doc.data() }));

    const result = {
      policies,
      lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
      hasMore: snapshot.docs.length === pageSize
    };

    // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, result, 24 * 60 * 60 * 1000);

    return result;
  }
);

export const addPolicy = createAsyncThunk(
  'policies/addPolicy',
  async ({ agentId, policyData }) => {
    const collectionRef = collection(db, 'agents', agentId, 'policies');
    const docRef = await addDoc(collectionRef, policyData);

    return serializePolicy({ id: docRef.id, ...policyData });
  }
);

export const fetchPolicyById = createAsyncThunk(
  'policies/fetchPolicyById',
  async ({ agentId, id }) => {
    const cacheKey = `policy_${agentId}_${id}`;
    const cachedData = firebaseCache.get(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    const docRef = doc(db, 'agents', agentId, 'policies', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      console.warn(`❌ Policy with ID ${id} not found under agent ${agentId}`);
      return null;
    }

    const result = serializePolicy({ id: docSnap.id, ...docSnap.data() });

    // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, result, 24 * 60 * 60 * 1000);

    return result;
  }
);

export const updatePolicy = createAsyncThunk('policies/updatePolicy', async ({ agentId, id, updates }) => {
  const docRef = doc(db, 'agents', agentId, 'policies', id);
  await updateDoc(docRef, updates);

  // Mark policy cache for invalidation (will be cleared on next read)
  firebaseCache.markForInvalidation('policies');
  firebaseCache.markForInvalidation(`policy_${agentId}`);

  return { id, updates };
});

const policySlice = createSlice({
  name: 'policies',
  initialState: {
    policies: [],
    currentPolicy: null,
    loading: false,
    error: null,
    lastDoc: null,
    hasMore: true,
    initialized: false, // Track if data has been loaded at least once
  },
  reducers: {
    clearCurrentPolicy(state) {
      state.currentPolicy = null;
    },
    clearPolicies(state) {
      state.policies = [];
    },
    clearPolicyCache: (state, action) => {
      const agentId = action.payload?.agentId;
      firebaseCache.clearPolicies(agentId);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicies.pending, (state) => {
        // Only show loader if we have no data
        if (state.policies.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        // Check if this is a paginated response (has policies array) or direct array
        if (Array.isArray(action.payload)) {
          // Direct array of policies (from non-paginated call)
          state.policies = action.payload;
        } else {
          // Paginated response object
          const { policies, lastDoc, hasMore } = action.payload;
          if (state.lastDoc) {
            // Continuation of pagination
            state.policies = [...state.policies, ...policies];
          } else {
            // Fresh pagination call
            state.policies = policies;
          }
          state.lastDoc = lastDoc;
          state.hasMore = hasMore;
        }
        state.loading = false;
        state.initialized = true; // Mark as initialized after first load
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPoliciesForMultipleUsers.pending, (state) => {
        // Only show loader if we have no data
        if (state.policies.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchPoliciesForMultipleUsers.fulfilled, (state, action) => {
        // Only update policies reference when data actually changed.
        // Skipping the assignment when both are empty prevents re-render loops
        // caused by cached thunks returning new [] references on every call.
        const incoming = action.payload;
        if (incoming.length > 0 || state.policies.length > 0) {
          state.policies = incoming;
        }
        state.loading = false;
        state.initialized = true;
      })
      .addCase(fetchPoliciesForMultipleUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPoliciesPaginated.fulfilled, (state, action) => {
        const { policies, lastDoc, hasMore } = action.payload;
        state.policies = [...state.policies, ...policies];
        state.lastDoc = lastDoc;
        state.hasMore = hasMore;
        state.loading = false;
      })
      .addCase(fetchPolicyById.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentPolicy = action.payload;
        } else {
          console.warn("📭 No policy found to set in currentPolicy");
        }
      })
      .addCase(addPolicy.fulfilled, (state, action) => {
        state.policies.unshift(action.payload);
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        const { id, updates } = action.payload;

        // Update in the main policies list
        const index = state.policies.findIndex((p) => p.id === id);
        if (index >= 0) {
          state.policies[index] = { ...state.policies[index], ...updates };
        }

        // Also update currentPolicy if it's the one being edited
        if (state.currentPolicy && state.currentPolicy.id === id) {
          state.currentPolicy = { ...state.currentPolicy, ...updates };
        }
      });
  },
});

export const { clearCurrentPolicy, clearPolicies, clearPolicyCache } = policySlice.actions;
export default policySlice.reducer;